"use client"

import { useCallback } from "react"
import { useRouter } from "next/navigation"
import { nativeBr } from "@/utils/nativeBr"

/**
 * URL 네비게이션을 처리하는 커스텀 훅
 *
 * @example
 * ```tsx
 * const { navigate } = useNewsNavigation()
 *
 * // 상대 경로
 * navigate("/challenges/123")
 *
 * // 절대 URL (같은 도메인)
 * navigate("https://library.kyobo.com/product/123")
 *
 * // 절대 URL (다른 도메인)
 * navigate("https://example.com")
 *
 * // DGT 경로 (네이티브: openDGTUrl / 웹: www.kyobo.com으로 오픈)
 * navigate("/dgt/web/customer/faq")
 * navigate("https://library.kyobo.com/dgt/web/customer/faq")
 * ```
 *
 * @returns {{ navigate: (url: string) => void }}
 */
export const useNewsNavigation = () => {
  const router = useRouter()

  const navigate = useCallback(
    (url: string) => {
      console.log("url:", url, "nativeBr.isEnabled:", nativeBr.isEnabled)

      const isAbsoluteUrl = url.startsWith("http")
      const isDgtPath = url.includes("/dgt")

      // /dgt 경로 처리
      if (isDgtPath) {
        const dgtPath = isAbsoluteUrl
          ? new URL(url).pathname + new URL(url).search + new URL(url).hash
          : url
        console.log("DGT path:", dgtPath)

        if (nativeBr.isEnabled) {
          const isLibraryDomain =
            window.location.hostname.includes("library.kyobo.com")
          const targetUrl = isLibraryDomain
            ? `https://app.kyobo.com${dgtPath}`
            : `https://t.kyobo.com${dgtPath}`
          nativeBr.openDGTUrl({ targetUrl })
        } else {
          // 웹 환경: https://www.kyobo.com 붙여서 외부 브라우저로 열기
          window.open(`https://www.kyobo.com${dgtPath}`, "_blank")
        }
        return
      }

      // 절대 URL 처리
      if (isAbsoluteUrl) {
        const isSameDomain = checkSameDomain(url)
        console.log("isSameDomain:", isSameDomain)

        if (isSameDomain) {
          // 같은 도메인: 경로 추출 후 router.push
          const urlObj = new URL(url)
          const path = urlObj.pathname + urlObj.search + urlObj.hash
          router.push(path)
        } else {
          // 다른 도메인
          if (nativeBr.isEnabled) {
            nativeBr.openExternalUrl({ targetUrl: url })
          } else {
            window.open(url, "_blank")
          }
        }
        return
      }

      // 상대 경로 처리
      router.push(url)
    },
    [router],
  )

  return { navigate }
}

const checkSameDomain = (url: string): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin)
    const getRootDomain = (hostname: string) => {
      const parts = hostname.split(".")
      return parts.length >= 2 ? parts.slice(-2).join(".") : hostname
    }

    return (
      urlObj.origin === window.location.origin ||
      getRootDomain(window.location.hostname) === getRootDomain(urlObj.hostname)
    )
  } catch {
    return true // 상대 경로는 같은 도메인으로 간주
  }
}
